import { useCallback, useRef, useContext, useEffect } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { motion } from 'framer-motion'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import { useDispatch, useSelector } from 'react-redux'
import { Prompt } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { Paper } from '@components'
import { GridFooter } from '@components/grid-footer'
import { DatasetsHeader } from '@components/headers/datasets'
import { INNER_PAGE_HEIGHT_WHEADER } from '@components/layout'
import { Status, headerTitleIsInvalid } from '@components/layout/headerNav'
import { PageHeaderContext } from '@contexts/pageHeader'
import { CreateDatasetContext } from '@datasets/reducer/context'
import * as SnackbarActions from '@global/snackbar/snackbar.actions'
import { api } from 'api'
import { usePasteNotification } from 'handsontable/hooks/usePasteNotification'
import { ROUTES, routeByParam } from 'routes'
import { ResubmitToolbar } from './toolbar'
import { useSubmissionStoreEffect } from './useSubmissionStoreEffect'
import { useEffectPreventReload } from '../create-dataset/useEffectPreventReload'
import { SubmissionTable } from '../handsontable/submission-table/submission-table'
import { moveToCellWithError } from '../utils'
import { t } from '@lingui/core/macro'
import { trackWarning } from 'lib/eventTracking'
import propTypes from 'prop-types'

export const ResubmitDataset = ({ match, history }) => {
  const dsId = match.params.datasetId
  const dispatch = useDispatch()
  const tableRef = useRef(null)
  const binderRef = useRef(null)
  const isMounted = useRef(true)
  const { state, dispatch: dispatchState } = useContext(CreateDatasetContext)

  const {
    state: { breadcrumbs },
    dispatch: pageHeaderDispatch,
  } = useContext(PageHeaderContext)

  const {
    templateId,
    unsavedChanges,
    errorCount,
    errors,
    blockSubmit,
    isSubmitting,
    isPasting,
    loading,
    dataContainer,
    dataset,
    submissionName,
  } = state

  const datasetMeta = useSelector(store => store.Datasets.datasetMetadataById[dsId])

  const submissionTemplate = useSelector(
    store => store.Datasets.submissionTemplatesById[templateId],
  )

  const requireSubmissionName = Boolean(submissionTemplate?.settings?.require_identifier)
  const isHeaderTitleInvalid = headerTitleIsInvalid({
    isRequired: submissionTemplate?.settings?.require_identifier,
    submissionName,
  })

  useEffect(
    () => () => {
      // resetStore when component unmounts
      isMounted.current = false
      dispatchState({ type: 'resetStore' })
    },
    [dispatchState],
  )

  useEffect(() => {
    isMounted.current = true
  }, [])

  const [DATASETS_PAGE_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )

  // Custom Hook to manage copy and paste.
  const { setCopyStatus } = usePasteNotification({
    isPasting,
    dispatch,
    dispatchState,
  })
  ResubmitDataset.propTypes = {
    match: propTypes.object.isRequired,
    history: propTypes.object.isRequired,
  }

  /**
   * This needs to be done because useSubmissionStoreEffect is setting the pending rows
   * and creating our dataContainer without any data. By doing this, we ensure that
   * the dataContainer has the data from the dataset right after the HandsOnTableBinder
   * is created and after pendingRows is finished, prevent the dataContainer.data from being empty.
   */
  function updateDatasetData(_updatedDataset) {
    if (
      !dataContainer ||
      !binderRef.current ||
      !submissionTemplate?.transformedColumns ||
      !isMounted.current
    )
      return

    const afterUpdateCallback = updatedDataContainer => {
      if (!isMounted.current) return

      dispatchState({
        type: 'setDataContainer',
        payload: updatedDataContainer,
      })

      dataContainer.forceRender()
    }

    dataContainer.updateDatasetDataAndTemplateFields({
      binderRef,
      dataset,
      transformedColumns: submissionTemplate?.transformedColumns,
      afterUpdateCallback,
    })
  }

  useEffectPreventReload({ block: unsavedChanges })

  useSubmissionStoreEffect(
    {
      dsId,
      setDatasetCallback: ({
        dataset: dataSetCallbackData,
        subTitle,
        submissionName,
        templateId,
      }) => {
        if (!isMounted.current) return

        dispatchState({
          type: 'setDataset',
          payload: { dataset: dataSetCallbackData, subTitle, submissionName, templateId },
        })
      },
      setPendingRowsCallback: ({ dataContainer: dc, dataset }) => {
        if (!isMounted.current) return

        dispatchState({
          type: 'setPendingRows',
          payload: { dataContainer: dc },
        })

        updateDatasetData(dataset)
      },
    },
    { dataset, submissionTemplate, templateId, dsId },
  )

  // Set page header configuration
  useEffect(() => {
    const folderName = submissionTemplate?.folder?.name

    if (!folderName && !submissionTemplate?.name) return

    const currBreadcrumbs = [
      ...(DATASETS_PAGE_ENABLED
        ? [{ label: t`Datasets`, path: '/' }]
        : [{ label: t`Templates`, path: '/codes/templates' }]),
      folderName ? { label: folderName } : null,
      {
        label: submissionTemplate?.name,
        path: routeByParam({ url: ROUTES.TEMPLATE_DATA, params: { id: templateId } }),
      },
    ].filter(p => p)

    // Only if breadcrumbs haven't been set; avoid infinite loop.
    if (isEqual(breadcrumbs, currBreadcrumbs)) return

    pageHeaderDispatch({
      type: 'setBreadcrumbs',
      payload: currBreadcrumbs,
    })

    return () => {
      pageHeaderDispatch({
        type: 'setBreadcrumbs',
        payload: [],
      })
    }
  }, [submissionTemplate?.folder?.name, submissionTemplate?.name, templateId])

  const onErrorClick = useCallback(() => {
    moveToCellWithError({ tableRef, binderRef, errors })
  }, [tableRef, binderRef, errors])

  const setNoUnsavedChangesCallback = useCallback(() => {
    dispatchState({ type: 'setStatus', payload: { unsavedChanges: false } })
  }, [dispatchState])

  const onUserChange = useCallback(() => {
    dispatchState({ type: 'setStatus', payload: { unsavedChanges: true } })
  }, [dispatchState])

  const onInvalidCellsUpdatedCallback = useCallback(
    errors => {
      dispatchState({ type: 'invalidCells', payload: { errors } })
    },
    [dispatchState],
  )

  const setTableRefCallback = useCallback(
    ref => {
      tableRef.current = ref
    },
    [tableRef.current],
  )
  const setBinderRefCallback = useCallback(
    ref => {
      binderRef.current = ref
    },
    [binderRef.current],
  )

  const makeDatasetPayload = useCallback(() => {
    const tableData = !isEmpty(dataContainer) && dataContainer?.getNonEmptyRows()
    if (!tableData.length) {
      return null
    }

    // Grab the field IDs on the first row and assume they are the same for all other rows.
    const headers = Object.values(tableData[0])
      .filter(cell => !cell.renderOnly && cell.field_id)
      .map(cell => cell.field_id)

    // Loop over all the data and create a two-dimensional array of arrays (CSV-esque data structure)
    const rows = tableData.map(row => {
      const values = Object.values(row)
        .filter(cell => !cell.renderOnly && cell.field_id)
        .map(cell => cell.cell_value)
      return values
    })

    return {
      datasetId: Number(dsId) || null,
      templateId: Number(templateId),
      ...(dataset.commitRef && { commitRef: dataset.commitRef }),
      submissionName,
      headers,
      rows,
    }
  }, [dataContainer, dsId, dataset, submissionName, templateId])

  /**
   * Submit the dataset to the resubmit endpoint.
   *
   * For unknown reasons, and in rare cases, duplicate CVIDs are making their way into the submission.
   * It is not clear how this is happening, it's possible that it's during the resubmission;
   * since we've not identified the exact cause, we'll prevent the duplicate CVID rows from
   * being submitted on the frontend and backend. See JIRA ticket CUST-2706.
   * - Notify user to reload the page and try again.
   * - Capture the error in Sentry.
   */
  const submitViaDataPlatform = async () => {
    /**
     * Checking for duplicate CVIDs in the submission.
     * If no duplicates, continue and begin submitting the dataset.
     */
    const tableData = !isEmpty(dataContainer) && dataContainer.getNonEmptyRows()
    const firstHeader = tableData?.length > 0 && Object.keys(tableData[0])[0]
    const listOfCVIDS = new Set() // Unique set of CVIDs.
    const tableDataCVIDs = [] // Resubmit can include empty CVID's on Submissions in Draft status.
    tableData.forEach(row => {
      const rowCVID = row[firstHeader].cvid

      // Only check against CVIDs that are not empty.
      if (rowCVID) {
        tableDataCVIDs.push(rowCVID)
        listOfCVIDS.add(rowCVID)
      }
    })
    const areDuplicatesPresent = listOfCVIDS.size !== tableDataCVIDs.length

    if (areDuplicatesPresent) {
      dispatch(
        SnackbarActions.showSnackbar({
          message: t`Duplicate CVIDs are present in the submission. Reload the page and try again.`,
          type: 'ERROR',
        }),
      )

      trackWarning('Duplicate CVIDs are present in the submission.', {
        datasetId: dsId,
        tableData,
      })

      return
    }

    const payload = makeDatasetPayload()

    if (!payload) {
      dispatch(
        SnackbarActions.showSnackbar({
          message: t`Please enter some data to save.`,
          type: 'ERROR',
        }),
      )
      return
    }

    try {
      await api.post('/api/v1/account/dataset/submit', payload)

      if (!isMounted.current) return

      dispatchState({ type: 'setStatus', payload: { unsavedChanges: false, isSubmitting: false } })

      dispatch(
        SnackbarActions.showSnackbar({
          message: t`Submission updated successfully`,
          type: 'SUCCESS',
        }),
      )
      history.push(`/codes/details/${dsId}/data`)
    } catch (e) {
      if (!isMounted.current) return

      dispatchState({ type: 'setStatus', payload: { unsavedChanges: false, isSubmitting: false } })

      // IF it is a validation error THEN display the default error message
      if (e?.status === 400 && e?.response?.data?.errors) {
        e.response.data.errors.forEach(err => {
          dispatch(
            SnackbarActions.showSnackbar({
              message: err.defaultErrorMessage,
              type: 'ERROR',
            }),
          )
        })
      }

      // IF it is another kind of error THEN display its message
      if (e?.status?.toString().startsWith(4) && e?.response?.data?.message) {
        dispatch(
          SnackbarActions.showSnackbar({
            message: e.response.data.message,
            type: 'ERROR',
          }),
        )
      }

      // At a minimum throw an error so we know something went wrong; don't swallow it
      throw e
    }
  }

  const onSubmit = () => {
    if (isHeaderTitleInvalid) return

    dispatchState({ type: 'setStatus', payload: { isSubmitting: true, unsavedChanges: false } })
    submitViaDataPlatform()
  }

  const debouncedEditableTitle = debounce(value => {
    dispatchState({ type: 'setUnblockSubmit' })
    dispatchState({ type: 'setSubmissionName', payload: value })
  }, 300)

  const onBlurCallback = useCallback(_value => {
    dispatchState({ type: 'setUnblockSubmit' })
  }, [])

  const onFocusCallback = useCallback(() => {
    dispatchState({ type: 'setBlockSubmit' })
  }, [])

  const submitDisabled =
    loading || isSubmitting || isPasting || blockSubmit || isHeaderTitleInvalid || errorCount > 0

  return (
    <>
      <motion.div
        className="edit-dataset"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.5 } }}
        exit={{ opacity: 0, transition: { duration: 0.5 } }}
        style={{ height: '100vh' }}
      >
        <Paper elevation={2} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <DatasetsHeader
            templateId={templateId}
            status={Status.UPDATE_ROWS}
            title={submissionName}
            editableTitleCallback={debouncedEditableTitle}
            editableTitleRequired={requireSubmissionName}
            editableDefaultTitle={t`Untitled Submission`}
            titleValidationError={isHeaderTitleInvalid}
            onBlurCallback={onBlurCallback}
            onFocusCallback={onFocusCallback}
          />
          <div
            style={{
              flex: 1,
              borderBottom: '1px solid rgb(204, 204, 204)',
              backgroundColor: '#f6f6f6',
            }}
          >
            <ResubmitToolbar
              hasDataset={Boolean(datasetMeta && Object.keys(datasetMeta)?.length)}
              submit={onSubmit}
              onErrorClick={onErrorClick}
              templateId={templateId}
              tableRef={tableRef}
              updatedAt={datasetMeta?.updated_at}
              submitDisabled={submitDisabled}
            />
            <div
              id="HOTContainer"
              style={{ width: '100%', height: INNER_PAGE_HEIGHT_WHEADER, position: 'relative' }}
              className="submission-table"
            >
              <div
                id="handsontable-container"
                className="handsontable-container"
                style={{ height: '100%' }}
              >
                {!loading ? (
                  <SubmissionTable
                    dataContainer={dataContainer}
                    dataset={dataset}
                    template={submissionTemplate}
                    hasUnsavedChanges={false}
                    isResubmit={true}
                    onUserChange={onUserChange}
                    onInvalidCellsUpdated={onInvalidCellsUpdatedCallback}
                    setTableRef={setTableRefCallback}
                    tableRef={tableRef.current}
                    setBinderRef={setBinderRefCallback}
                    dataLoaded={setNoUnsavedChangesCallback}
                    setCopyStatus={setCopyStatus}
                  />
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress color="primary" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Paper>
        <Prompt
          when={unsavedChanges}
          message={t`You are about to lose any unsaved changes by leaving this page. Are you sure you want to continue?`}
        />
      </motion.div>
      <GridFooter datasetId={dsId} isEditing displayedRowsCount={dataContainer?.data?.length} />
    </>
  )
}
